// Binance WebSocket + REST helpers (Spot + Futures)
const SPOT_REST = 'https://api.binance.com/api/v3';
const SPOT_WS = 'wss://stream.binance.com:9443';
const FUT_REST = 'https://fapi.binance.com/fapi/v1';
const FUT_WS = 'wss://fstream.binance.com';

const rest = (market) => (market === 'futures' ? FUT_REST : SPOT_REST);
const wsBase = (market) => (market === 'futures' ? FUT_WS : SPOT_WS);

export const SYMBOLS = [
  { symbol: 'BTCUSDT', display: 'BTC/USDT', pricePrecision: 2 },
  { symbol: 'ETHUSDT', display: 'ETH/USDT', pricePrecision: 2 },
  { symbol: 'SOLUSDT', display: 'SOL/USDT', pricePrecision: 3 },
  { symbol: 'BNBUSDT', display: 'BNB/USDT', pricePrecision: 2 },
  { symbol: 'XRPUSDT', display: 'XRP/USDT', pricePrecision: 4 },
  { symbol: 'ADAUSDT', display: 'ADA/USDT', pricePrecision: 4 },
  { symbol: 'DOGEUSDT', display: 'DOGE/USDT', pricePrecision: 5 },
  { symbol: 'AVAXUSDT', display: 'AVAX/USDT', pricePrecision: 3 },
  { symbol: 'LINKUSDT', display: 'LINK/USDT', pricePrecision: 3 },
  { symbol: 'MATICUSDT', display: 'MATIC/USDT', pricePrecision: 4 },
  { symbol: 'DOTUSDT', display: 'DOT/USDT', pricePrecision: 3 },
  { symbol: 'LTCUSDT', display: 'LTC/USDT', pricePrecision: 2 }
];

export const TIMEFRAMES = [
  { label: '1m', value: '1m' },
  { label: '5m', value: '5m' },
  { label: '15m', value: '15m' },
  { label: '1h', value: '1h' },
  { label: '4h', value: '4h' },
  { label: '1d', value: '1d' }
];

export async function fetchKlines(symbol, interval = '1m', limit = 300, market = 'spot') {
  const res = await fetch(`${rest(market)}/klines?symbol=${symbol}&interval=${interval}&limit=${limit}`);
  if (!res.ok) throw new Error('Failed to fetch klines');
  const data = await res.json();
  return data.map(k => ({
    time: Math.floor(k[0] / 1000),
    open: parseFloat(k[1]),
    high: parseFloat(k[2]),
    low: parseFloat(k[3]),
    close: parseFloat(k[4]),
    volume: parseFloat(k[5])
  }));
}

export async function fetch24hr(symbol, market = 'spot') {
  const res = await fetch(`${rest(market)}/ticker/24hr?symbol=${symbol}`);
  if (!res.ok) throw new Error('Failed 24hr');
  return res.json();
}

export async function fetchDepth(symbol, limit = 20, market = 'spot') {
  const res = await fetch(`${rest(market)}/depth?symbol=${symbol}&limit=${limit}`);
  if (!res.ok) throw new Error('Failed depth');
  return res.json();
}

export function subscribeKline(symbol, interval, onMsg, market = 'spot') {
  const s = symbol.toLowerCase();
  const ws = new WebSocket(`${wsBase(market)}/ws/${s}@kline_${interval}`);
  ws.onmessage = (ev) => {
    const m = JSON.parse(ev.data);
    if (m.k) {
      onMsg({
        time: Math.floor(m.k.t / 1000),
        open: parseFloat(m.k.o),
        high: parseFloat(m.k.h),
        low: parseFloat(m.k.l),
        close: parseFloat(m.k.c),
        volume: parseFloat(m.k.v),
        closed: m.k.x
      });
    }
  };
  return ws;
}

export function subscribeTicker(symbol, onMsg, market = 'spot') {
  const ws = new WebSocket(`${wsBase(market)}/ws/${symbol.toLowerCase()}@ticker`);
  ws.onmessage = (ev) => {
    const m = JSON.parse(ev.data);
    onMsg({
      symbol: m.s,
      last: parseFloat(m.c),
      change: parseFloat(m.p),
      changePct: parseFloat(m.P),
      high: parseFloat(m.h),
      low: parseFloat(m.l),
      volume: parseFloat(m.v),
      quoteVolume: parseFloat(m.q),
      bid: parseFloat(m.b || m.c),
      ask: parseFloat(m.a || m.c)
    });
  };
  return ws;
}

export function subscribeDepth(symbol, onMsg, market = 'spot') {
  const stream = market === 'futures' ? `${symbol.toLowerCase()}@depth20@100ms` : `${symbol.toLowerCase()}@depth20@100ms`;
  const ws = new WebSocket(`${wsBase(market)}/ws/${stream}`);
  ws.onmessage = (ev) => {
    const m = JSON.parse(ev.data);
    onMsg({
      bids: (m.bids || m.b || []).map(([p, q]) => ({ price: parseFloat(p), qty: parseFloat(q) })),
      asks: (m.asks || m.a || []).map(([p, q]) => ({ price: parseFloat(p), qty: parseFloat(q) }))
    });
  };
  return ws;
}

export function subscribeTrades(symbol, onMsg, market = 'spot') {
  const evName = market === 'futures' ? 'aggTrade' : 'trade';
  const ws = new WebSocket(`${wsBase(market)}/ws/${symbol.toLowerCase()}@${evName}`);
  ws.onmessage = (ev) => {
    const m = JSON.parse(ev.data);
    onMsg({
      id: m.a || m.t,
      price: parseFloat(m.p),
      qty: parseFloat(m.q),
      time: m.T,
      buyerMaker: m.m
    });
  };
  return ws;
}

// Multi-symbol ticker via combined stream
export function subscribeMultiTicker(symbols, onMsg, market = 'spot') {
  const streams = symbols.map(s => `${s.toLowerCase()}@ticker`).join('/');
  const ws = new WebSocket(`${wsBase(market)}/stream?streams=${streams}`);
  ws.onmessage = (ev) => {
    const outer = JSON.parse(ev.data);
    const m = outer.data;
    if (!m) return;
    onMsg({
      symbol: m.s,
      last: parseFloat(m.c),
      changePct: parseFloat(m.P),
      high: parseFloat(m.h),
      low: parseFloat(m.l),
      quoteVolume: parseFloat(m.q)
    });
  };
  return ws;
}

export function fmtPrice(v, prec = 2) {
  if (v === undefined || v === null || isNaN(v) || v === 0) return '-';
  return v.toLocaleString('en-US', { minimumFractionDigits: prec, maximumFractionDigits: prec });
}
